// Módulo Operacional — Onda Op-1: lista de contratos + detalhe básico.
//
// REFACTOR 2026-05-23 (Wave 2): este arquivo era 7013 linhas. Após a Wave 2
// ficou com o dispatcher + ListaContratos + SecaoProjetos + ContratoDetalhe.
// Toda lógica de abas internas está em `operacional-contratos/`.
//
// Já extraídos pra operacional-contratos/:
//   - types.ts                  (interfaces + constants)
//   - utils.ts                  (fmt/num/getToken/caches de API/etc)
//   - shared-components.tsx     (Card, Linha, KPI, BarraProgresso)
//   - AbaBriefing.tsx
//   - AbaAditivos.tsx
//   - AbaEntregaveis.tsx        (+ ModalVincularParcelas)
//   - EditarContrato.tsx
//   - VisaoGeral.tsx            (+ MiniKpisContrato + CardArrastavel + LinhaEditavel*)
//   - Timeline.tsx
//   - Drive.tsx                 (+ ModalVersoesDrive + ModalContatosDrive + ModalCompartilharDrive)
//   - Atividades.tsx            (+ ListaAtividades)
//   - AtividadesKanban.tsx
//   - AtividadesGantt.tsx
//   - AtividadesCalendario.tsx
//   - ModalAtividade.tsx        (+ RecursosAtividade)
//   - AbaArts.tsx
//   - ModalArt.tsx              (+ BlocoEndereco + ListaLinhasAtividade + SecaoArt + form helpers)
//   - ModalArtCopiar.tsx        (+ SecaoCopia + CampoCopia)
//   - AbaFinanceiro.tsx         (+ ModalEmitirNfProjeto + ModalNovaParcela + ModalReorganizarParcelas + ModalMoverParcela + ModalLinkBmProjeto)
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  App as AntApp, Typography, Card, Statistic, Row, Col, Input, Select, Tag,
  Table, Button, Modal, Form, Checkbox, Empty, Spin, Space, Flex, Tabs, Result,
  Tooltip, Divider, Alert, Segmented, Dropdown,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SearchOutlined, FilterOutlined, FolderOpenOutlined,
  TeamOutlined, ArrowLeftOutlined, DeleteOutlined,
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  CalendarOutlined, DownOutlined, ColumnWidthOutlined,
} from '@ant-design/icons';
import { useColunasRedimensionaveis, useControleLarguras } from '../utils/colunasRedimensionaveis';
import { usePerms } from '../hooks/usePerms';
import OperacionalControleEntregas from './OperacionalControleEntregas';
import OperacionalMapaGeral from './OperacionalMapaGeral';
import OperacionalFormalidades from './OperacionalFormalidades';
import { API_URL } from '../config';
import MapaContrato from '../components/MapaContrato';
import { AbaDiariosCampo } from './operacional-contratos/AbaDiariosCampo';
// Tipos e helpers extraídos
import {
  STATUS_CONTRATO,
  KPI_CARDS,
} from './operacional-contratos/types';
import type {
  Contrato, Atividade, KpiBucket, DashboardProjetos,
  CampoOrd, AbaContrato,
} from './operacional-contratos/types';
import {
  fmt, num, fmtData, getToken, prazoDias,
} from './operacional-contratos/utils';
import { AbaBriefing } from './operacional-contratos/AbaBriefing';
import { AbaAditivos } from './operacional-contratos/AbaAditivos';
import { AbaEntregaveis } from './operacional-contratos/AbaEntregaveis';
import { EditarContrato } from './operacional-contratos/EditarContrato';
import { VisaoGeral } from './operacional-contratos/VisaoGeral';
import { Timeline } from './operacional-contratos/Timeline';
import { Drive } from './operacional-contratos/Drive';
import { AbaDadosCampo } from './operacional-contratos/AbaDadosCampo';
import { Atividades } from './operacional-contratos/Atividades';
import { AbaArts } from './operacional-contratos/AbaArts';
import { AbaFinanceiroResumo } from './operacional-contratos/AbaFinanceiro';
import { DreContratoView } from '../components/DreContratoView';
import { AbaAtestados } from './operacional-contratos/AbaAtestados';
import { EquipeContrato } from './operacional-contratos/EquipeContrato';
import {
  PlanoTrabalhoView, ResumoPlanoTrabalho, usePlanoTrabalho,
} from './operacional-contratos/PlanoTrabalho';

export default function OperacionalContratos() {
  return (
    <AntApp>
      <OperacionalContratosInner />
    </AntApp>
  );
}

type VistaContratos = 'lista' | 'entregas' | 'mapa' | 'formalidades';

// Vistas do topo de Contratos. Controle de Entregas entrou em 2026-06-19 e o
// Mapa Geral em 2026-08-21 — os dois saíram do menu lateral porque são jeitos
// de olhar a MESMA carteira de contratos, não módulos à parte.
// Lista é `perm: null` porque quem chega nesta página já passou pelo gate de
// `operacional.projetos.listar`.
const VISTAS: { value: VistaContratos; label: string; perm: string | null }[] = [
  { value: 'lista',    label: 'Lista de Contratos',      perm: null },
  { value: 'entregas', label: '📊 Controle de Entregas', perm: 'operacional.controle_entregas.ver' },
  { value: 'mapa',     label: '🗺️ Mapa Geral',           perm: 'operacional.mapa_geral.ver' },
  // FORMALIDADES (2026-08-24): venda → instrumento no Jurídico → ART → CAT,
  // uma linha por contrato — o lugar de tratar formalidade sem caçar contrato.
  { value: 'formalidades', label: '📋 Formalidades',       perm: 'operacional.formalidades.ver' },
];

function OperacionalContratosInner() {
  const { pode } = usePerms();
  const [searchParams, setSearchParams] = useSearchParams();
  const vistasVisiveis = VISTAS.filter(v => !v.perm || pode(v.perm));

  // A vista vem da URL (?vista=) e cai na última usada. A URL é o que faz o
  // link antigo /operacional/mapa-geral continuar levando ao mapa, e de quebra
  // torna a vista compartilhável.
  const vistaPedida = (searchParams.get('vista') || '') as VistaContratos;
  const vistaSalva = (() => {
    try { return (sessionStorage.getItem('op:vista_contratos') || '') as VistaContratos; }
    catch { return '' as VistaContratos; }
  })();
  const permitida = (v: string) => vistasVisiveis.some(x => x.value === v);
  const vista: VistaContratos = permitida(vistaPedida) ? vistaPedida
    : permitida(vistaSalva) ? vistaSalva : 'lista';

  function setVista(v: VistaContratos) {
    try { sessionStorage.setItem('op:vista_contratos', v); } catch {}
    const next = new URLSearchParams(searchParams);
    next.set('vista', v);
    setSearchParams(next, { replace: true });
  }
  useEffect(() => {
    try { sessionStorage.setItem('op:vista_contratos', vista); } catch {}
  }, [vista]);
  // 2026-06-09: persiste contrato aberto no sessionStorage pra F5 voltar
  // pro mesmo contrato (em vez da lista de contratos).
  const [contratoAberto, setContratoAberto] = useState<string | null>(() => {
    try { return searchParams.get('contrato') || sessionStorage.getItem('op:contrato_aberto') || null; }
    catch { return null; }
  });
  useEffect(() => {
    try {
      if (contratoAberto) sessionStorage.setItem('op:contrato_aberto', contratoAberto);
      else sessionStorage.removeItem('op:contrato_aberto');
    } catch {}
  }, [contratoAberto]);
  useEffect(() => {
    if (!contratoAberto || searchParams.get('contrato')) return;
    const next = new URLSearchParams(searchParams);
    next.set('contrato', contratoAberto);
    setSearchParams(next, { replace: true });
  }, [contratoAberto, searchParams, setSearchParams]);

  function abrirContrato(contratoId: string) {
    const next = new URLSearchParams(searchParams);
    if (contratoId !== contratoAberto) next.delete('aba');
    next.set('contrato', contratoId);
    setSearchParams(next, { replace: true });
    setContratoAberto(contratoId);
  }

  function voltarAosContratos() {
    const next = new URLSearchParams(searchParams);
    next.delete('contrato');
    next.delete('aba');
    setSearchParams(next, { replace: true });
    setContratoAberto(null);
  }

  // Auto-abre contrato se localStorage tem 'op:abrir_contrato_id' (vindo de
  // "Iniciar contrato" no comercial). Limpa após ler pra não reabrir em F5.
  useEffect(() => {
    try {
      const id = localStorage.getItem('op:abrir_contrato_id');
      if (id) {
        localStorage.removeItem('op:abrir_contrato_id');
        abrirContrato(id);
      }
    } catch {}
    // Esse redirect é consumido uma única vez; mudanças seguintes de contrato
    // passam por abrirContrato e atualizam a URL diretamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (contratoAberto) {
    return <ContratoDetalhe id={contratoAberto} onVoltar={voltarAosContratos}
             onAbrir={abrirContrato} />;
  }
  // Só a lista disponível: sem alternador de uma opção só.
  if (vistasVisiveis.length < 2) {
    return <ListaContratos onAbrir={abrirContrato} />;
  }
  return (
    <>
      {/* overflow-x: com 4 vistas o Segmented (~590px, não encolhe nem quebra
          linha) transborda entre ~768–900px de largura — justamente a faixa
          tablet que o CLAUDE.md exige. Rolagem própria em vez de cortar a
          última opção, que seria a recém-lançada. */}
      <div style={{ padding: '12px 24px 0', overflowX: 'auto' }}>
        <Segmented
          value={vista}
          onChange={(v) => setVista(v as VistaContratos)}
          options={vistasVisiveis.map(v => ({ label: v.label, value: v.value }))}
        />
      </div>
      {/* Renderiza SÓ a vista ativa: o Mapa Geral é MapLibre com todas as
          geometrias de todos os contratos — montar junto com a lista custa caro. */}
      {vista === 'entregas' && <OperacionalControleEntregas />}
      {vista === 'formalidades' && <OperacionalFormalidades onAbrir={abrirContrato} />}
      {vista === 'mapa'     && <OperacionalMapaGeral />}
      {vista === 'lista'    && <ListaContratos onAbrir={abrirContrato} />}
    </>
  );
}

// ════════════════ LISTA ════════════════
// KpiBucket, DashboardProjetos e KPI_CARDS vêm de types.ts agora.

function ListaContratos({ onAbrir }: { onAbrir: (id: string) => void }) {
  const { pode } = usePerms();
  const { message } = AntApp.useApp();
  const podeEditarEquipe = pode('operacional.contrato.visao.editar_equipe_tecnica');
  // Alterar status direto na lista (sem entrar no contrato): liberado pra quem
  // edita QUALQUER contrato (admin/diretor). Gestores/coords seguem pelo
  // detalhe (onde o backend resolve o vínculo). O backend valida de toda forma.
  const podeEditarStatus = pode('operacional.projetos.editar_qualquer');
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroCliente, setFiltroCliente] = useState<string>('');
  const [dashboard, setDashboard] = useState<DashboardProjetos | null>(null);
  const [filtroKpi, setFiltroKpi] = useState<KpiBucket | null>(null);
  // Controle do "Restaurar colunas" — o hook irmão daquele que a tabela usa,
  // compartilhando a mesma chave (a tabela vive no componente filho).
  const { ajustado: larguraAjustada, restaurar: restaurarLarguras } =
    useControleLarguras('op:contratos:larguras');

  // Onda C3: seleção em batch pra atribuir gestor/coordenador em N contratos.
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [mostrarModalBatch, setMostrarModalBatch] = useState(false);
  const [gestorBatch, setGestorBatch] = useState('');
  const [coordBatch, setCoordBatch] = useState('');
  const [sobrescrever, setSobrescrever] = useState(false);
  const [salvandoBatch, setSalvandoBatch] = useState(false);

  function toggleSelecionado(id: string) {
    setSelecionados(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function toggleTodos(ids: string[]) {
    setSelecionados(prev => {
      const todosMarcados = ids.length > 0 && ids.every(id => prev.has(id));
      const n = new Set(prev);
      if (todosMarcados) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  }
  function limparSelecao() { setSelecionados(new Set()); }

  async function aplicarBatch() {
    if (selecionados.size === 0) return;
    const gestor = gestorBatch.trim().toLowerCase();
    const coord = coordBatch.trim().toLowerCase();
    if (!gestor && !coord) {
      alert('Preencha pelo menos gestor ou coordenador.');
      return;
    }
    if (gestor && !gestor.includes('@')) { alert('Email de gestor inválido.'); return; }
    if (coord && !coord.includes('@')) { alert('Email de coordenador inválido.'); return; }
    const n = selecionados.size;
    const linhas = [
      `Vai aplicar em ${n} contrato(s):`,
      '',
      gestor ? `• Gestor: ${gestor}` : null,
      coord ? `• Coordenador: ${coord}` : null,
      '',
      sobrescrever
        ? 'Modo: SOBRESCREVER (substitui valores já preenchidos).'
        : 'Modo: preencher só onde estiver vazio.',
      '',
      'Confirma?',
    ].filter(Boolean).join('\n');
    if (!window.confirm(linhas)) return;
    setSalvandoBatch(true);
    try {
      const token = await getToken();
      const resp = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atribuir_equipe_batch_contratos',
          contrato_ids: Array.from(selecionados),
          gestor_email: gestor || undefined,
          coordenador_email: coord || undefined,
          sobrescrever },
        { headers: { Authorization: token } });
      const data = resp.data.body ? JSON.parse(resp.data.body) : resp.data;
      const atualizados = data.contratos_atualizados ?? 0;
      const vinculos = data.vinculos_criados ?? 0;
      const ignorados = data.ignorados?.length || 0;
      const msg = [
        'Concluído.',
        `• ${atualizados} contrato(s) atualizado(s)`,
        `• ${vinculos} novo(s) vínculo(s) criado(s) em contrato_equipe`,
        ignorados ? `• ${ignorados} ignorado(s)` : null,
      ].filter(Boolean).join('\n');
      alert(msg);
      setMostrarModalBatch(false);
      setGestorBatch(''); setCoordBatch(''); setSobrescrever(false);
      limparSelecao();
      await carregar();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvandoBatch(false);
    }
  }

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_listar_contratos' },
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_dashboard_projetos' },
          { headers: { Authorization: token } }),
      ]);
      const data = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      setContratos(Array.isArray(data) ? data : []);
      const dash = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setDashboard(dash);
    } finally { setLoading(false); }
  }

  // Altera o status de um contrato direto da lista (sem abrir o detalhe).
  // Otimista: atualiza a linha na hora; recarrega depois pra ressincronizar os
  // contadores dos cards (e reverter se o backend recusar).
  async function alterarStatusInline(id: string, novo: string) {
    setContratos(prev => prev.map(c => c.id === id ? { ...c, status: novo } : c));
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_contrato', id, status: novo },
        { headers: { Authorization: token } });
      message.success(`Status → ${(STATUS_CONTRATO[novo] || {}).label || novo}`);
      carregar();
    } catch (e: any) {
      message.error('Erro ao mudar status: ' + (e.response?.data?.error || e.message));
      carregar();
    }
  }

  // Ordenação por header (clique no th alterna asc/desc)
  // Default: HY26032 primeiro, HY24001 último (mais recente → mais antigo).
  const [ordenarPor, setOrdenarPor] = useState<CampoOrd>('numero');
  const [ordenarDir, setOrdenarDir] = useState<'asc'|'desc'>('desc');
  function alternarOrdem(c: CampoOrd) {
    if (ordenarPor === c) setOrdenarDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setOrdenarPor(c); setOrdenarDir('asc'); }
  }

  // Detecta se número é código formal (HY24034, HY24023-15) vs descrição livre
  // (PRAD FSJ, Estudo de Inundação Pelotas) — separamos em 2 seções na lista.
  const ehCodigoHY = (n: string) => /^HY/i.test(n);

  const idsFiltroKpi: Set<string> | null = (filtroKpi && dashboard)
    ? new Set(dashboard[filtroKpi].ids) : null;

  // Opções do filtro de cliente: derivadas dos contratos já carregados, não de
  // uma chamada nova ao cadastro. Assim a lista mostra só quem de fato tem
  // contrato (~90 de 477 cadastrados) e cada opção já vem com a contagem.
  // Agrupa por NOME, não por id: cliente duplicado no cadastro (mesmo nome, ids
  // diferentes) apareceria duas vezes e cada linha traria metade dos contratos.
  // (objeto simples em vez de Map: o target do TS do projeto não permite
  // espalhar Map.entries() sem --downlevelIteration)
  const clientesComContrato = (() => {
    const cont: Record<string, number> = {};
    contratos.forEach(c => {
      const nome = (c.cliente_nome || '').trim();
      if (nome) cont[nome] = (cont[nome] || 0) + 1;
    });
    return Object.keys(cont)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map(nome => ({ value: nome, label: `${nome} (${cont[nome]})` }));
  })();

  const filtrados = contratos.filter(c => {
    if (idsFiltroKpi && !idsFiltroKpi.has(c.id)) return false;
    if (filtroStatus && c.status !== filtroStatus) return false;
    if (filtroCliente && (c.cliente_nome || '').trim() !== filtroCliente) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return c.numero.toLowerCase().includes(q)
          || c.titulo.toLowerCase().includes(q)
          || (c.cliente_nome || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Comparador pra ordenação. Trata null/undefined/strings/numbers de forma sensata.
  const cmp = (a: Contrato, b: Contrato): number => {
    const dir = ordenarDir === 'asc' ? 1 : -1;
    let va: any, vb: any;
    switch (ordenarPor) {
      case 'numero':     va = a.numero || '';                          vb = b.numero || ''; break;
      case 'titulo':     va = a.titulo || '';                          vb = b.titulo || ''; break;
      case 'cliente':    va = a.cliente_nome || '';                    vb = b.cliente_nome || ''; break;
      case 'gestor':     va = a.gestor_email || '';                    vb = b.gestor_email || ''; break;
      case 'coord':      va = a.coordenador_email || '';               vb = b.coordenador_email || ''; break;
      case 'status':     va = a.status || '';                          vb = b.status || ''; break;
      case 'valor':      va = num(a.valor_contratado) + num(a.aditivos_valor_soma);
                         vb = num(b.valor_contratado) + num(b.aditivos_valor_soma); break;
      case 'atividades': va = a.atividades_abertas || 0;                vb = b.atividades_abertas || 0; break;
      case 'conclusao':  va = a.data_termino_previsto || '';            vb = b.data_termino_previsto || ''; break;
      case 'prazo_dias': va = prazoDias(a) ?? -1;                       vb = prazoDias(b) ?? -1; break;
    }
    if (va === vb) return 0;
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb), 'pt-BR') * dir;
  };

  // Segregação: 2 grupos
  const grupoFormais = filtrados.filter(c => ehCodigoHY(c.numero)).sort(cmp);
  const grupoLivres  = filtrados.filter(c => !ehCodigoHY(c.numero)).sort(cmp);
  const [mostrarLivres, setMostrarLivres] = useState(true);

  // Counts por status (tags clicáveis abaixo do filtro).
  // A base respeita o filtro de CLIENTE mas ignora o de status — senão, ao
  // clicar em "Em execução", todas as outras tags zerariam e não haveria como
  // voltar. Com cliente escolhido, as contagens passam a ser daquele cliente,
  // que é o que a pessoa quer saber ao filtrar por ele.
  const baseContagem = filtroCliente
    ? contratos.filter(c => (c.cliente_nome || '').trim() === filtroCliente)
    : contratos;
  const statusCounts: Array<{ key: string; label: string; count: number; cor: string; bg: string }> = !loading && baseContagem.length > 0
    ? Object.entries(STATUS_CONTRATO).map(([k, v]) => ({
        key: k, label: v.label, cor: v.cor, bg: v.bg,
        count: baseContagem.filter(c => c.status === k).length,
      })).filter(s => s.count > 0)
    : [];

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Header */}
      <Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
        <Space align="baseline">
          <Typography.Title level={3} style={{ margin: 0, color: '#020D7F' }}>
            <FolderOpenOutlined /> Contratos
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {contratos.length} contrato(s) — briefing, execução, atestado e ARTs
          </Typography.Text>
        </Space>
      </Flex>

      {/* KPIs — Card clicáveis */}
      {dashboard && (
        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
          {KPI_CARDS.map(card => {
            const ativo = filtroKpi === card.key;
            const valor = dashboard[card.key].count;
            const disabled = valor === 0 && !ativo;
            return (
              <Col key={card.key} xs={12} sm={8} md={6} lg={4} xl={3}>
                <Card size="small" hoverable={!disabled}
                  onClick={() => !disabled && setFiltroKpi(prev => prev === card.key ? null : card.key)}
                  styles={{ body: { padding: 12 } }}
                  style={{
                    borderTop: `3px solid ${ativo ? card.cor : 'transparent'}`,
                    background: ativo ? card.cor : (disabled ? '#FAFAFA' : '#fff'),
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                  }}>
                  <Flex align="center" gap={6} style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>{card.emoji}</span>
                    <Typography.Text style={{
                      fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: ativo ? '#fff' : (disabled ? '#bbb' : card.cor),
                    }}>{card.label}</Typography.Text>
                  </Flex>
                  <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1,
                    color: ativo ? '#fff' : (disabled ? '#bbb' : '#222') }}>
                    {valor}
                  </div>
                  {ativo && (
                    <Typography.Text style={{ fontSize: 9, color: '#fff', opacity: 0.85, display: 'block', marginTop: 4 }}>
                      Filtrado · clique pra remover
                    </Typography.Text>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Filtros */}
      <Flex gap={10} wrap="wrap" style={{ marginBottom: 12 }}>
        <Input.Search allowClear placeholder="Buscar número, título, cliente..."
          value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined />}
          style={{ flex: 1, minWidth: 240, maxWidth: 380 }} />
        <Select value={filtroStatus || ''} onChange={(v) => setFiltroStatus(v || '')}
          placeholder="Todos status" style={{ width: 180 }} allowClear
          suffixIcon={<FilterOutlined />}
          options={Object.entries(STATUS_CONTRATO).map(([k, v]) => ({ value: k, label: v.label }))} />
        {/* Filtro por cliente: só entram clientes que REALMENTE têm contrato
            (~90), não os 477 do cadastro — a maior parte é lead criado por
            automação e nunca contratou. Com busca, porque 90 numa lista é
            muito pra rolar. A contagem ajuda a achar os clientes grandes. */}
        <Select value={filtroCliente || undefined} onChange={(v) => setFiltroCliente(v || '')}
          placeholder="Todos os clientes" style={{ width: 280 }} allowClear showSearch
          suffixIcon={<FilterOutlined />}
          filterOption={(entrada, opt) =>
            String(opt?.label ?? '').toLowerCase().includes(entrada.toLowerCase())}
          options={clientesComContrato} />
        {filtroKpi && (
          <Button danger onClick={() => setFiltroKpi(null)}>
            Remover filtro KPI
          </Button>
        )}
        {larguraAjustada && (
          <Tooltip title="Você ajustou a largura das colunas. Isso volta ao padrão.">
            <Button icon={<ColumnWidthOutlined />} onClick={restaurarLarguras}>
              Restaurar colunas
            </Button>
          </Tooltip>
        )}
      </Flex>

      {/* Resumo por status — tags clicáveis */}
      {statusCounts.length > 0 && (
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 12 }}>
          {statusCounts.map(s => (
            <Tag key={s.key}
              onClick={() => setFiltroStatus(filtroStatus === s.key ? '' : s.key)}
              style={{
                cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px',
                background: s.bg, color: s.cor,
                borderColor: filtroStatus === s.key ? s.cor : 'transparent',
                borderWidth: filtroStatus === s.key ? 2 : 0,
              }}>
              {s.label}: {s.count}
            </Tag>
          ))}
        </Flex>
      )}

      {/* Batch action bar */}
      {podeEditarEquipe && selecionados.size > 0 && (
        <Alert
          type="info" showIcon
          style={{ marginBottom: 12, background: '#EEF1FF', borderColor: '#0930D0' }}
          message={
            <Flex align="center" gap={12}>
              <Typography.Text strong style={{ color: '#020D7F' }}>
                {selecionados.size} contrato(s) selecionado(s)
              </Typography.Text>
              <div style={{ flex: 1 }} />
              <Button type="primary" onClick={() => setMostrarModalBatch(true)}>
                Atribuir gestor/coordenador ({selecionados.size})
              </Button>
              <Button onClick={limparSelecao}>Limpar</Button>
            </Flex>
          }
        />
      )}

      {/* Conteúdo: spinner / empty / sections */}
      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}><Spin /></Flex>
      ) : filtrados.length === 0 ? (
        <Empty description={contratos.length === 0
          ? 'Nenhum contrato ainda. Use o botão "🚀 Iniciar contrato" na aba Vendas de um negócio com status "Ganho".'
          : 'Nenhum contrato corresponde ao filtro.'} />
      ) : (
        <>
          {/* Seção 1: projetos com código HY formal */}
          <SecaoProjetos
            titulo="Contratos com código HY"
            subtitulo={`${grupoFormais.length} contrato(s) com numeração padrão (HY{ano}{seq})`}
            contratos={grupoFormais}
            onAbrir={onAbrir}
            ordenarPor={ordenarPor} ordenarDir={ordenarDir} alternarOrdem={alternarOrdem}
            selecionados={selecionados}
            onToggleSelecionado={toggleSelecionado}
            onToggleTodos={toggleTodos}
            podeSelecionar={podeEditarEquipe}
            podeEditarStatus={podeEditarStatus} onAlterarStatus={alterarStatusInline}
          />

          {/* Seção 2: identificadores livres — colapsável */}
          {grupoLivres.length > 0 && (
            <Card size="small" style={{ marginTop: 20, background: '#FFF8E8', borderColor: '#FFE9C2' }}>
              <Flex align="center" gap={10}>
                <span style={{ fontSize: 16 }}>✏️</span>
                <div style={{ flex: 1 }}>
                  <Typography.Text strong style={{ color: '#B07500', fontSize: 13 }}>
                    Identificadores livres ({grupoLivres.length})
                  </Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Contratos sem código HY formal — texto descritivo no lugar do número (provém do Notion).
                    </Typography.Text>
                  </div>
                </div>
                <Button size="small" onClick={() => setMostrarLivres(v => !v)}
                  style={{ borderColor: '#B07500', color: '#B07500' }}>
                  {mostrarLivres ? 'Ocultar' : 'Mostrar'}
                </Button>
              </Flex>
              {mostrarLivres && (
                <div style={{ marginTop: 10 }}>
                  <SecaoProjetos
                    titulo=""
                    subtitulo=""
                    contratos={grupoLivres}
                    onAbrir={onAbrir}
                    ordenarPor={ordenarPor} ordenarDir={ordenarDir} alternarOrdem={alternarOrdem}
                    livre
                    selecionados={selecionados}
                    onToggleSelecionado={toggleSelecionado}
                    onToggleTodos={toggleTodos}
                    podeSelecionar={podeEditarEquipe}
                    podeEditarStatus={podeEditarStatus} onAlterarStatus={alterarStatusInline}
                  />
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Modal batch — atribuir gestor/coordenador em N contratos selecionados */}
      <Modal open={mostrarModalBatch}
        title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
          Atribuir gestor / coordenador em batch
        </Typography.Title>}
        onCancel={() => !salvandoBatch && setMostrarModalBatch(false)}
        confirmLoading={salvandoBatch}
        okText={salvandoBatch ? 'Aplicando…' : 'Aplicar'}
        okButtonProps={{ disabled: !gestorBatch.trim() && !coordBatch.trim() }}
        onOk={aplicarBatch}
        width={480}>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 14 }}>
          Aplica em <strong>{selecionados.size} contrato(s)</strong> selecionado(s).
          Atualiza o campo no contrato e cria o vínculo correspondente em <code>contrato_equipe</code> (matriz de permissões).
        </Typography.Paragraph>
        <Form layout="vertical" disabled={salvandoBatch}>
          <Form.Item label="Gestor (email)">
            <Input type="email" value={gestorBatch} onChange={e => setGestorBatch(e.target.value)}
              placeholder="ex: fulano@hydrodataeng.com.br" />
          </Form.Item>
          <Form.Item label="Coordenador (email)">
            <Input type="email" value={coordBatch} onChange={e => setCoordBatch(e.target.value)}
              placeholder="ex: fulano@hydrodataeng.com.br" />
          </Form.Item>
          <Form.Item>
            <Checkbox checked={sobrescrever} onChange={e => setSobrescrever(e.target.checked)}>
              Sobrescrever valores já preenchidos
            </Checkbox>
            <div style={{ fontSize: 10, color: '#888', marginLeft: 22 }}>
              Se desmarcado, só preenche contratos onde o campo estiver vazio (padrão seguro).
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ─── SUB-COMPONENTE: tabela com header ordenável (reutilizada nas 2 seções) ──
// (CampoOrd vem de types.ts agora; prazoDias vem de utils.ts.)

// Soma das larguras padrão das colunas. Serve de min-width da tabela: abaixo
// disso rola horizontalmente, acima ela estica pra ocupar a tela.
// ⚠ Se mexer nas larguras das colunas, ajuste este número junto — se ele ficar
//   MAIOR que a soma, aparece rolagem sem necessidade; MENOR, sobra buraco.
const LARGURA_MINIMA_TABELA = 1200;

function SecaoProjetos({
  titulo, subtitulo, contratos, onAbrir, ordenarPor, ordenarDir, alternarOrdem, livre,
  selecionados, onToggleSelecionado, onToggleTodos, podeSelecionar,
  podeEditarStatus, onAlterarStatus,
}: {
  titulo: string; subtitulo: string;
  contratos: Contrato[]; onAbrir: (id: string) => void;
  ordenarPor: CampoOrd; ordenarDir: 'asc'|'desc'; alternarOrdem: (c: CampoOrd) => void;
  livre?: boolean;
  selecionados: Set<string>;
  onToggleSelecionado: (id: string) => void;
  onToggleTodos: (ids: string[]) => void;
  podeSelecionar: boolean;
  podeEditarStatus?: boolean;
  onAlterarStatus?: (id: string, novo: string) => void;
}) {
  // Larguras ajustáveis pelo usuário, persistidas por navegador. A chave é a
  // mesma nas duas seções (formais e livres) de propósito: são a mesma lista
  // partida em duas, e larguras diferentes ficariam visualmente quebradas.
  //
  // ⚠ ANTES do `return null` abaixo: hook não pode ficar depois de early
  //   return — a seção "livres" some quando não há contratos livres, e a ordem
  //   dos hooks mudaria entre renders.
  const { aplicar: aplicarLarguras, componentes } =
    useColunasRedimensionaveis<Contrato>('op:contratos:larguras');

  if (contratos.length === 0) return null;

  const nomeDoEmail = (e?: string) => e ? e.split('@')[0] : '—';

  // Helper pra sortOrder do Antd Table (ascend/descend)
  const sortOrderOf = (campo: CampoOrd): 'ascend' | 'descend' | undefined =>
    ordenarPor === campo ? (ordenarDir === 'asc' ? 'ascend' : 'descend') : undefined;

  // sorter:true delega a ordenação pro state externo via onChange (que alterna).
  const columns: ColumnsType<Contrato> = [
    {
      title: 'Número', key: 'numero', dataIndex: 'numero', width: 110,
      sorter: true, sortOrder: sortOrderOf('numero'),
      render: (numero: string, c) => (
        <span style={{
          fontFamily: livre ? 'inherit' : 'monospace',
          fontStyle: livre ? 'italic' : 'normal',
          color: livre ? '#B07500' : '#020D7F',
          fontWeight: 600,
        }} title={numero}>
          {numero}
          {c.aditivos_total ? (
            <Tooltip title={`${c.aditivos_total} aditivo(s)`}>
              <Tag color="orange" style={{ marginLeft: 6, fontSize: 9, padding: '0 5px', lineHeight: '14px' }}>
                +{c.aditivos_total}
              </Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      // width explícita nas duas colunas flexíveis: sem um valor de partida a
      // alça de arrasto não tem de onde sair (o antd só informa a largura que
      // a coluna declara, não a calculada).
      title: 'Contrato', key: 'titulo', dataIndex: 'titulo', width: 230,
      sorter: true, sortOrder: sortOrderOf('titulo'),
      ellipsis: true,
      render: (titulo: string) => <span style={{ fontWeight: 500 }}>{titulo}</span>,
    },
    {
      title: 'Cliente', key: 'cliente', dataIndex: 'cliente_nome', width: 180,
      sorter: true, sortOrder: sortOrderOf('cliente'),
      ellipsis: true,
      render: (n?: string) => n
        ? <Typography.Text style={{ color: '#555' }}>{n}</Typography.Text>
        : <Typography.Text type="secondary" italic>(sem cliente)</Typography.Text>,
    },
    {
      title: 'Gerente', key: 'gestor', dataIndex: 'gestor_email', width: 110,
      sorter: true, sortOrder: sortOrderOf('gestor'),
      render: (e?: string) => <Tooltip title={e}>
        <Typography.Text style={{ fontSize: 11, color: e ? '#555' : '#bbb' }}>{nomeDoEmail(e)}</Typography.Text>
      </Tooltip>,
    },
    {
      title: 'Coordenador', key: 'coord', dataIndex: 'coordenador_email', width: 115,
      sorter: true, sortOrder: sortOrderOf('coord'),
      render: (e?: string) => <Tooltip title={e}>
        <Typography.Text style={{ fontSize: 11, color: e ? '#555' : '#bbb' }}>{nomeDoEmail(e)}</Typography.Text>
      </Tooltip>,
    },
    {
      title: 'Status', key: 'status', dataIndex: 'status', width: 112, align: 'center',
      sorter: true, sortOrder: sortOrderOf('status'),
      render: (s: string, c: Contrato) => {
        const st = STATUS_CONTRATO[s] || STATUS_CONTRATO.planejamento;
        const tag = (
          <Tag style={{ fontSize: 10, fontWeight: 600, background: st.bg, color: st.cor,
            borderColor: st.cor + '40', marginInlineEnd: 0, cursor: podeEditarStatus ? 'pointer' : 'default' }}>
            {st.label}{podeEditarStatus && <DownOutlined style={{ fontSize: 8, marginLeft: 4, opacity: 0.7 }} />}
          </Tag>
        );
        if (!podeEditarStatus) return tag;
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              selectedKeys: [s],
              items: Object.entries(STATUS_CONTRATO).map(([k, v]) => ({
                key: k,
                label: <span style={{ color: v.cor, fontWeight: k === s ? 700 : 400 }}>{v.label}</span>,
              })),
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key !== s) onAlterarStatus?.(c.id, key);
              },
            }}
          >
            {/* span com stopPropagation: não dispara o onRow (abrir contrato) */}
            <span onClick={(e) => e.stopPropagation()}>{tag}</span>
          </Dropdown>
        );
      },
    },
    {
      title: 'Valor', key: 'valor', width: 110, align: 'right',
      sorter: true, sortOrder: sortOrderOf('valor'),
      render: (_, c) => {
        const valorTotal = num(c.valor_contratado) + num(c.aditivos_valor_soma);
        return (
          <div>
            <Typography.Text strong>{valorTotal ? fmt(valorTotal) : '—'}</Typography.Text>
            {(c.aditivos_valor_soma || 0) !== 0 && (
              <div style={{ fontSize: 9, color: '#B07500' }}>base {fmt(c.valor_contratado || 0)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Conclusão prev.', key: 'conclusao', dataIndex: 'data_termino_previsto',
      width: 108, align: 'center',
      sorter: true, sortOrder: sortOrderOf('conclusao'),
      render: (d?: string) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmtData(d)}</Typography.Text>,
    },
    {
      title: 'Prazo', key: 'prazo_dias', width: 70, align: 'center',
      sorter: true, sortOrder: sortOrderOf('prazo_dias'),
      render: (_, c) => {
        const dias = prazoDias(c);
        return <Typography.Text style={{ fontSize: 11, color: '#666' }}>{dias != null ? `${dias} d` : '—'}</Typography.Text>;
      },
    },
    {
      title: 'Ativ.', key: 'atividades', dataIndex: 'atividades_abertas', width: 55, align: 'center',
      sorter: true, sortOrder: sortOrderOf('atividades'),
      render: (n?: number) => <Typography.Text style={{ fontSize: 11, color: '#666' }}>{n ? n : '—'}</Typography.Text>,
    },
  ];

  // Map column key → CampoOrd
  const keyToCampo: Record<string, CampoOrd> = {
    numero: 'numero', titulo: 'titulo', cliente: 'cliente', gestor: 'gestor',
    coord: 'coord', status: 'status', valor: 'valor', conclusao: 'conclusao',
    prazo_dias: 'prazo_dias', atividades: 'atividades',
  };

  const rowSelection = podeSelecionar ? {
    selectedRowKeys: contratos.filter(c => selecionados.has(c.id)).map(c => c.id),
    onSelect: (record: Contrato) => onToggleSelecionado(record.id),
    onSelectAll: (selected: boolean, _selectedRows: Contrato[], _changeRows: Contrato[]) => {
      onToggleTodos(contratos.map(c => c.id));
    },
  } : undefined;

  return (
    <div style={{ marginTop: titulo ? 8 : 0 }}>
      {titulo && (
        <Space align="baseline" style={{ marginBottom: 8 }}>
          <Typography.Title level={5} style={{ margin: 0, color: '#020D7F', fontSize: 13, fontWeight: 600 }}>{titulo}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{subtitulo}</Typography.Text>
        </Space>
      )}
      <Table<Contrato>
        rowKey="id"
        size="small"
        columns={aplicarLarguras(columns)}
        components={componentes}
        // tableLayout fixed é o que faz o navegador RESPEITAR a largura
        // declarada; sem isso ele recalcula pelo conteúdo e o arrasto não
        // parece ter efeito.
        tableLayout="fixed"
        // ⚠ x NUMÉRICO, não 'max-content': com número o antd usa isso como
        //   min-width e a tabela ESTICA pra ocupar a tela quando sobra espaço.
        //   Com 'max-content' ela fica travada na soma das larguras e cria
        //   rolagem horizontal SEMPRE — foi assim que metade das colunas saiu
        //   da tela e não dava pra alcançar a alça delas (13/08/2026).
        scroll={{ x: LARGURA_MINIMA_TABELA }}
        dataSource={contratos}
        pagination={false}
        rowSelection={rowSelection}
        onRow={(c) => ({
          onClick: () => onAbrir(c.id),
          style: { cursor: 'pointer', background: selecionados.has(c.id) ? '#EEF1FF' : undefined },
        })}
        onChange={(_pag, _filt, sorter) => {
          // Sorter pode ser objeto único ou array. Pegamos só o campo principal.
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          if (s?.columnKey) {
            const campo = keyToCampo[s.columnKey as string];
            if (campo) alternarOrdem(campo);
          }
        }}
      />
    </div>
  );
}

// ════════════════ DETALHE DO CONTRATO ════════════════
// AbaContrato vem de types.ts
function ContratoDetalhe({ id, onVoltar, onAbrir }: {
  id: string; onVoltar: () => void;
  // Troca o contrato aberto sem passar pela lista — usado pra descer no aditivo
  // (aba Aditivos) e pra voltar dele ao contrato pai. A lista esconde aditivos
  // por padrão (contrato_pai_id IS NULL), então ela não serve de caminho.
  onAbrir?: (contratoId: string) => void;
}) {
  const { pode } = usePerms();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  // 2026-06-09: persiste aba ativa no sessionStorage (key: op:aba:<contrato_id>)
  // pra F5 voltar na mesma aba
  // 2026-09-13: a aba também vem da URL (?aba=), para link direto (ex.: ?aba=plano).
  // Aba sem permissão (ou antes de as permissões carregarem) cai na Visão geral
  // NA RENDERIZAÇÃO, sem apagar a escolha — ver `abaAtiva` abaixo.
  const abaInicial = (): AbaContrato => {
    try {
      return (searchParams.get('aba') as AbaContrato | null)
        || (sessionStorage.getItem(`op:aba:${id}`) as AbaContrato) || 'visao';
    } catch { return 'visao'; }
  };
  const [aba, setAba] = useState<AbaContrato>(abaInicial);
  useEffect(() => {
    setAba(abaInicial());
    // A troca de contrato precisa escolher a aba daquele contrato, sem herdar
    // a que estava aberta no contrato anterior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    try { sessionStorage.setItem(`op:aba:${id}`, aba); } catch {}
  }, [id, aba]);
  const [eventos, setEventos] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [linkPubAberto, setLinkPubAberto] = useState(false);
  const planoHabilitado = pode('operacional.plano.ver');
  const planoController = usePlanoTrabalho(id, planoHabilitado);

  // Depois do segundo aceite do plano, só a lista de atividades muda: recarrega
  // ela sem o spinner de página inteira do carregar().
  async function recarregarAtividades() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_atividades', contrato_id: id }, { headers: { Authorization: token } });
      const ats = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (Array.isArray(ats)) setAtividades(ats);
    } catch (e: any) {
      console.warn('Falha ao recarregar atividades após o plano:', e.message);
    }
  }

  function selecionarAba(proxima: AbaContrato) {
    setAba(proxima);
    const next = new URLSearchParams(searchParams);
    next.set('contrato', id);
    next.set('aba', proxima);
    setSearchParams(next, { replace: true });
  }

  useEffect(() => { carregar(); }, [id]);

  // Refresh-on-focus desativado em 2026-05-26: estava resetando o subtree
  // do contrato (e perdendo state de pasta/upload no Drive) quando o file
  // picker do OS fechava — o browser ganhava foco, refetch disparava
  // carregar(), setContrato(novoObj) criava referência nova, Drive.tsx
  // (useEffect em [contrato.id]) re-rodava recarregar() e a pasta ativa
  // voltava pra "Comercial". Upload abortado silenciosamente.
  // Pra reativar com segurança, debounce de 30s + comparar conteúdo antes
  // do setContrato pra não disparar re-renders em foco curto.

  async function carregar() {
    setLoading(true);
    setErroCarga(null);
    try {
      const token = await getToken();
      // Sprint UI-fix: separa o GET principal das listas auxiliares.
      // Antes: Promise.all sem catch → qualquer 404 derrubava tudo e deixava
      // contrato=null silenciosamente, mostrando "Contrato não encontrado"
      // sem indicar a causa (ID stale, contrato deletado, erro de rede).
      let r1: any;
      try {
        r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_obter_contrato', id }, { headers: { Authorization: token } });
      } catch (e: any) {
        const status = e.response?.status;
        const msg = e.response?.data?.error || e.response?.data?.message || e.message;
        if (status === 404) {
          setErroCarga(`Contrato "${id}" não foi encontrado (pode ter sido excluído ou o link está desatualizado).`);
        } else {
          setErroCarga(`Erro ao carregar contrato: ${msg}`);
        }
        setContrato(null);
        return;
      }
      setContrato(r1.data.body ? JSON.parse(r1.data.body) : r1.data);
      // Listas auxiliares: se uma falhar, o contrato ainda renderiza com a aba vazia.
      try {
        const [r2, r3, r4] = await Promise.all([
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_listar_eventos', contrato_id: id }, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_listar_documentos', contrato_id: id }, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_listar_atividades', contrato_id: id }, { headers: { Authorization: token } }),
        ]);
        const evs = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
        setEventos(Array.isArray(evs) ? evs : []);
        const ds = r3.data.body ? JSON.parse(r3.data.body) : r3.data;
        setDocs(Array.isArray(ds) ? ds : []);
        const ats = r4.data.body ? JSON.parse(r4.data.body) : r4.data;
        setAtividades(Array.isArray(ats) ? ats : []);
      } catch (e: any) {
        console.warn('Falha ao carregar listas auxiliares do contrato:', e.message);
      }
    } finally { setLoading(false); }
  }

  // [cffa38df] Excluir contrato — usa op_excluir_contrato do backend.
  // Pede dupla confirmação porque é destrutivo (cascata em atividades+docs+eventos).
  async function excluirContrato() {
    if (!contrato) return;
    if (!window.confirm(`Excluir o contrato ${contrato.numero} — "${contrato.titulo}"?\n\nIsso remove TAMBÉM:\n• Todas as atividades vinculadas\n• Todos os planos de trabalho, versões e aprovações\n• Documentos do drive (referências, não os arquivos originais do negócio)\n• Eventos/timeline\n\nO negócio comercial e os anexos originais NÃO são afetados.\nAção irreversível.`)) return;
    const conf = window.prompt(`Para confirmar, digite o número do contrato: ${contrato.numero}`);
    if (conf !== contrato.numero) {
      if (conf !== null) alert('Número não confere — exclusão cancelada.');
      return;
    }
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_excluir_contrato', id: contrato.id },
        { headers: { Authorization: token } });
      alert('Contrato excluído.');
      onVoltar();
    } catch (e: any) {
      alert('Erro ao excluir: ' + (e.response?.data?.error || e.message));
    }
  }

  if (loading) return (
    <Flex justify="center" align="center" style={{ minHeight: 200 }}>
      <Spin tip="Carregando contrato..." size="large" />
    </Flex>
  );
  if (!contrato) return (
    <Result
      status="warning"
      title={erroCarga || 'Contrato não encontrado.'}
      subTitle={<Typography.Text code style={{ fontSize: 11 }}>ID: {id}</Typography.Text>}
      extra={[
        <Button key="back" type="primary" icon={<ArrowLeftOutlined />} onClick={onVoltar}>
          Voltar pra lista
        </Button>,
        <Button key="clear" onClick={() => { try { localStorage.removeItem('op:abrir_contrato_id'); } catch {}; onVoltar(); }}>
          Limpar redirect automático
        </Button>,
      ]}
    />
  );

  const st = STATUS_CONTRATO[contrato.status] || STATUS_CONTRATO.planejamento;

  const atividadesAbertas = atividades.filter(a => a.status !== 'concluida' && a.status !== 'cancelada').length;

  async function alterarStatus(novo: string) {
    if (!contrato || novo === contrato.status) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_contrato', id: contrato.id, status: novo },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Build Tabs items — só inclui aba se passar nos filtros de perm
  const tabsConfig: Array<{ id: AbaContrato; label: string; perm?: string; requerPodeEditar?: boolean; oculta?: boolean; render: () => React.ReactNode }> = [
    { id: 'visao',             label: 'Visão geral',
      render: () => <>
        {planoHabilitado && <ResumoPlanoTrabalho controller={planoController} abrirPlano={() => selecionarAba('plano')} />}
        <VisaoGeral contrato={contrato} onAtualizar={carregar}
          linkPubAberto={linkPubAberto} setLinkPubAberto={setLinkPubAberto} />
      </> },
    { id: 'plano',             label: 'Plano de trabalho',
      perm: 'operacional.plano.ver',
      render: () => <PlanoTrabalhoView controller={planoController}
        mostrarDadosCampo={pode('operacional.contrato.aba.dados_campo.ver')}
        navegar={selecionarAba} onAtividadesAlteradas={() => void recarregarAtividades()} /> },
    { id: 'briefing',          label: 'Briefing',
      render: () => <AbaBriefing contrato={contrato} /> },
    { id: 'entregaveis',       label: `Entregáveis${contrato.entregaveis_total ? ` (${contrato.entregaveis_total})` : ''}`,
      render: () => <AbaEntregaveis contrato={contrato} onAtualizar={carregar} /> },
    { id: 'atividades',        label: `Atividades${atividadesAbertas ? ` (${atividadesAbertas})` : ''}`,
      render: () => <Atividades contrato={contrato} atividades={atividades} onAtualizar={carregar} /> },
    { id: 'arts',              label: `ARTs${contrato.arts_total ? ` (${contrato.arts_total})` : ''}`,
      render: () => <AbaArts contrato={contrato} onAtualizar={carregar} /> },
    { id: 'aditivos',          label: `Aditivos${contrato.aditivos_total ? ` (${contrato.aditivos_total})` : ''}`,
      perm: 'operacional.contrato.aba.aditivos.ver',
      // Aditivo de aditivo não SE CRIA MAIS (opCriarAditivo e opIniciarContrato
      // recusam com 400), mas 9 existem em produção, anteriores à trava — o
      // HY24023-15 tem o HY24023-15-A1 pendurado nele. Esconder a aba num
      // aditivo vazio tira ruído; escondê-la num que TEM filho deixaria esses 9
      // netos inalcançáveis pela tela. Por isso a condição olha as duas coisas.
      // (O botão "Novo aditivo" some dentro da aba — ver AbaAditivos.)
      oculta: !!contrato.contrato_pai_id && !contrato.aditivos_total,
      render: () => <AbaAditivos contrato={contrato} onAtualizar={carregar} onAbrir={onAbrir} /> },
    { id: 'financeiro_resumo', label: 'Financeiro',
      perm: 'operacional.contrato.aba.financeiro.ver',
      render: () => <AbaFinanceiroResumo contrato={contrato} onAtualizar={carregar} /> },
    // FECHAMENTO-CC (2026-08-17): lucro real do contrato (receita − direto −
    // overhead − impostos), da decomposição da DRE geral. Gate provisório:
    // quem vê a DRE da empresa vê o resultado do contrato — perm dedicada
    // entra na Fase 4.
    { id: 'resultado',         label: '💰 Resultado',
      perm: 'financeiro.dre.ver',
      render: () => <div style={{ padding: 8 }}><DreContratoView contratoId={contrato.id} /></div> },
    { id: 'atestados', label: '📄 Atestado',
      perm: 'operacional.atestado.ver',
      render: () => <AbaAtestados contrato={contrato as any} onAtualizar={carregar} /> },
    { id: 'timeline',          label: 'Linha do tempo',
      render: () => <Timeline contrato={contrato} eventos={eventos} onAtualizar={carregar} /> },
    { id: 'drive',             label: `Drive (${docs.length})`,
      render: () => <Drive contrato={contrato} docs={docs} onAtualizar={carregar} /> },
    { id: 'mapa',              label: 'Mapa',
      render: () => <MapaContrato contratoId={contrato.id} /> },
    { id: 'diarios',           label: '📓 Diários',
      render: () => <AbaDiariosCampo contrato={contrato} /> },
    { id: 'dados_campo',       label: '📡 Dados de Campo',
      perm: 'operacional.contrato.aba.dados_campo.ver',
      render: () => <AbaDadosCampo contrato={contrato} /> },
    // KICKOFF (2026-08-20): esta tela existia desde 2026-06 e nunca foi montada
    // em lugar nenhum. É a ÚNICA visão de operacao.contrato_equipe — a tabela
    // que o gate podeEditarContrato realmente lê. Sem ela, "por que o Felipe
    // toma 403 se está como gestor no contrato?" só se responde no SQL.
    { id: 'equipe',            label: '👥 Equipe',
      render: () => <EquipeContrato contratoId={contrato.id} /> },
    { id: 'edit',              label: 'Editar dados', requerPodeEditar: true,
      render: () => <EditarContrato contrato={contrato} onSaved={carregar} /> },
  ];
  const abasVisiveis = tabsConfig
    .filter(t => !t.oculta)
    .filter(t => !t.perm || pode(t.perm))
    .filter(t => !t.requerPodeEditar || (contrato as any).pode_editar);
  const abaAtiva: AbaContrato = abasVisiveis.some(t => t.id === aba) ? aba : 'visao';
  const tabsItems = abasVisiveis
    .map(t => ({ key: t.id, label: t.label, children: abaAtiva === t.id ? t.render() : null }));

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Cabeçalho compacto: voltar + número + status + excluir */}
      <Flex align="center" gap={14} wrap="wrap" style={{ marginBottom: 12 }}>
        {/* Aditivo: o botão volta pro PAI, não pra lista — a lista esconde
            aditivos, então "voltar aos contratos" perderia o contexto de onde
            a pessoa veio. Quem quer a lista clica no número do pai... e depois
            em Voltar; por isso os dois caminhos ficam visíveis. */}
        {contrato.contrato_pai_id && onAbrir ? (
          <Button icon={<ArrowLeftOutlined />}
            onClick={() => onAbrir(contrato.contrato_pai_id as string)}>
            Voltar ao {contrato.pai_numero || 'contrato pai'}
          </Button>
        ) : (
          <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>
            Voltar aos contratos
          </Button>
        )}
        <Typography.Text strong style={{
          fontFamily: 'monospace', fontSize: 14, color: '#020D7F',
        }}>
          {contrato.contrato_pai_id && (
            <Typography.Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {contrato.pai_numero || '…'} ›{' '}
            </Typography.Text>
          )}
          {contrato.numero}
        </Typography.Text>
        {contrato.contrato_pai_id && (
          <Tag style={{ background: '#FFF8E8', color: '#B07500', border: 'none', fontWeight: 600, margin: 0 }}>
            Aditivo{contrato.ordem ? ` ${contrato.ordem}` : ''}
          </Tag>
        )}
        <Tooltip title="Alterar status">
          <Select value={contrato.status} onChange={alterarStatus} size="small"
            style={{ minWidth: 150 }}
            popupMatchSelectWidth={false}
            options={Object.entries(STATUS_CONTRATO).map(([k, v]) => ({
              value: k,
              label: <Tag style={{ background: v.bg, color: v.cor, border: 'none', fontWeight: 600, margin: 0 }}>{v.label}</Tag>,
            }))} />
        </Tooltip>
        <div style={{ flex: 1 }} />
        {pode('operacional.projetos.excluir') && (
          <Button danger icon={<DeleteOutlined />} onClick={excluirContrato}>
            Excluir contrato
          </Button>
        )}
      </Flex>

      {/* Título + sub-info */}
      <Typography.Title level={3} style={{ margin: '0 0 4px', color: '#222' }}>
        {contrato.titulo}
      </Typography.Title>
      <Space split={<Divider type="vertical" />} style={{ marginBottom: 12, fontSize: 13 }} wrap>
        <Typography.Text style={{ color: '#555' }}>{contrato.cliente_nome}</Typography.Text>
        {contrato.cliente_cnpj && <Typography.Text type="secondary">CNPJ {contrato.cliente_cnpj}</Typography.Text>}
        {contrato.contato_nome && <Typography.Text type="secondary">A/C {contrato.contato_nome}</Typography.Text>}
      </Space>

      {/* A ESPECIFICIDADE do aditivo. O briefing dele é herdado do pai (é o que
          opIniciarContrato copia ao criar), então o que distingue este aditivo
          do contrato-mãe é o motivo — e ele só existia na tabela do pai, sem
          aparecer em lugar nenhum ao abrir o aditivo. */}
      {contrato.contrato_pai_id && (
        <div style={{ background: '#FFF8E8', border: '0.5px solid #F0E0B8', borderRadius: 6,
          padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          <Typography.Text strong style={{ color: '#B07500' }}>
            Aditivo{contrato.ordem ? ` ${contrato.ordem}` : ''} de {contrato.pai_numero || 'contrato'}
          </Typography.Text>
          {contrato.pai_titulo && (
            <Typography.Text type="secondary"> — {contrato.pai_titulo}</Typography.Text>
          )}
          <div style={{ marginTop: 4, color: '#555' }}>
            <strong>Motivo:</strong>{' '}
            {contrato.motivo_aditivo?.trim()
              || <Typography.Text type="secondary" italic>não informado ao criar o aditivo</Typography.Text>}
          </div>
          <div style={{ marginTop: 2, fontSize: 11.5, color: '#888' }}>
            Briefing, equipe e escopo herdados do contrato pai. Edite aqui o que for
            específico deste aditivo — o que você mudar não volta pro pai.
          </div>
        </div>
      )}

      {/* Tabs Antd — itens filtrados por perm; renderiza só a aba ativa */}
      <Tabs activeKey={abaAtiva} onChange={(k) => selecionarAba(k as AbaContrato)} items={tabsItems} />
    </div>
  );
}
